import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { COLUMN_SIZE } from 'components/Table/constants'
import Tooltip from 'components/Tooltip'
import { ReportStatusChip } from 'features/yieldbirdWrapperAgent/components/ReportStatusChip'
import { displayUrl } from 'features/yieldbirdWrapperAgent/utils/url'
import { DATE_FORMAT, formatDate } from 'utils/date'
import { OpenFileButton } from './OpenFileButton'

import type { ColumnDef } from '@tanstack/react-table'
import type { Report, ReportFormat } from 'features/yieldbirdWrapperAgent/api/types'

const EMPTY_CELL = '-'

const renderFileCell = (report: Report, format: ReportFormat): React.JSX.Element | string =>
  report.data.includes(format) ? (
    <OpenFileButton
      jobId={report.jobId}
      kind={format}
    />
  ) : (
    EMPTY_CELL
  )

export const useColumns = (): ColumnDef<Report>[] => {
  const { t } = useTranslation('features/yieldbirdWrapperAgent')

  return [
    {
      accessorKey: 'createdAt',
      cell: ({ row }) => formatDate(new Date(row.original.createdAt), DATE_FORMAT.WITH_TIME, true),
      header: t('reports.header.createdAt'),
    },
    {
      accessorKey: 'url',
      cell: ({ row }) => (
        <Tooltip content={row.original.url}>
          <span>{displayUrl(row.original.url)}</span>
        </Tooltip>
      ),
      header: t('reports.header.url'),
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => (
        <FlexBox
          axis='x'
          sx={{ alignItems: 'center' }}
        >
          <ReportStatusChip status={row.original.status} />

          <Tooltip content={t(`status.${row.original.status}`)} />
        </FlexBox>
      ),
      header: t('reports.header.status'),
    },
    {
      accessorKey: 'diagnostics',
      cell: ({ row }) => (
        <OpenFileButton
          jobId={row.original.jobId}
          kind='status'
        />
      ),
      header: t('reports.header.diagnostics'),
      meta: { centered: true },
      size: COLUMN_SIZE.XS,
    },
    {
      accessorKey: 'html',
      cell: ({ row }) => renderFileCell(row.original, 'html'),
      header: t('reports.header.html'),
      meta: { centered: true },
      size: COLUMN_SIZE.XS,
    },
    {
      accessorKey: 'json',
      cell: ({ row }) => renderFileCell(row.original, 'json'),
      header: t('reports.header.json'),
      meta: { centered: true },
      size: COLUMN_SIZE.XS,
    },
  ]
}
